import { useState, createElement } from 'react';
import { PDFViewer, PDFDownloadLink, pdf } from '@react-pdf/renderer';
import { siniestrosService } from '../services/siniestrosService';
import InformeSiniestrosPdf from '../pdf/InformeSiniestrosPdf';

const hoy = new Date().toISOString().split('T')[0];
const inicioAnyo = `${new Date().getFullYear()}-01-01`;

const ESTADOS = [
  { key: 'emitido',   label: 'Emitido',    color: 'blue'   },
  { key: 'facturado', label: 'Confirmado',  color: 'green'  },
  { key: 'cobrado',   label: 'Cobrado',     color: 'purple' },
  { key: 'anulado',   label: 'Anulado',     color: 'red'    },
];

const COLOR_ACTIVE = {
  blue:   'bg-blue-50 border-blue-400 text-blue-700',
  green:  'bg-green-50 border-green-400 text-green-700',
  purple: 'bg-purple-50 border-purple-400 text-purple-700',
  red:    'bg-red-50 border-red-400 text-red-700',
};

export default function InformeSiniestrosPage() {
  const [desde, setDesde]     = useState(inicioAnyo);
  const [hasta, setHasta]     = useState(hoy);
  const [estados, setEstados] = useState({ emitido: false, facturado: false, cobrado: false, anulado: false });

  const [datos, setDatos]         = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [error, setError]         = useState(null);

  const toggleEstado = (key) => {
    setEstados(prev => ({ ...prev, [key]: !prev[key] }));
    setDatos(null);
  };

  const algunoSeleccionado = Object.values(estados).some(Boolean);

  const buildParams = () => {
    const params = { desde, hasta };
    if (algunoSeleccionado) {
      ESTADOS.forEach(({ key }) => { if (estados[key]) params[key] = true; });
    }
    return params;
  };

  const buildTitulo = () => algunoSeleccionado
    ? `SINIESTROS — ${ESTADOS.filter(e => estados[e.key]).map(e => e.label).join(' / ').toUpperCase()}`
    : 'INFORME DE SINIESTROS';

  // Desktop: carga datos y muestra en el visor
  const handleGenerar = async () => {
    setIsLoading(true);
    setLoadingMsg('Consultando datos...');
    setError(null);
    setDatos(null);
    try {
      const res = await siniestrosService.informe(buildParams());
      setDatos(res.data);
    } catch {
      setError('Error al generar el informe');
    } finally {
      setIsLoading(false);
      setLoadingMsg('');
    }
  };

  // Móvil: carga datos + genera PDF + descarga, todo en un paso
  const handleGenerarYDescargar = async () => {
    setIsLoading(true);
    setError(null);
    setDatos(null);
    try {
      setLoadingMsg('Consultando datos...');
      const res = await siniestrosService.informe(buildParams());
      const siniestros = res.data;
      setDatos(siniestros);

      setLoadingMsg('Generando PDF...');
      const titulo = buildTitulo();
      const subtitulo = `${desde} — ${hasta}`;
      const doc = createElement(InformeSiniestrosPdf, { siniestros, titulo, subtitulo });
      const blob = await pdf(doc).toBlob();

      setLoadingMsg('Descargando...');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `informe-siniestros-${desde}-${hasta}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Error al generar el informe');
    } finally {
      setIsLoading(false);
      setLoadingMsg('');
    }
  };

  const titulo    = buildTitulo();
  const subtitulo = `${desde} — ${hasta}`;
  const pdfDoc    = datos
    ? <InformeSiniestrosPdf siniestros={datos} titulo={titulo} subtitulo={subtitulo} />
    : null;

  const Filtros = (
    <>
      {/* Estado */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Estado</p>
        <p className="text-xs text-gray-400 mb-3">
          {algunoSeleccionado ? 'Siniestros con alguno de estos estados' : 'Sin filtro — todos los estados'}
        </p>
        <div className="space-y-2">
          {ESTADOS.map(({ key, label, color }) => (
            <button key={key} onClick={() => toggleEstado(key)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-sm font-medium transition
                ${estados[key] ? COLOR_ACTIVE[color] : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'}`}>
              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition
                ${estados[key] ? 'border-current bg-current' : 'border-gray-300'}`}>
                {estados[key] && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/>
                  </svg>
                )}
              </div>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Fechas */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Rango fecha facturado</p>
        <div className="space-y-2">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Desde</label>
            <input type="date" value={desde}
              onChange={e => { setDesde(e.target.value); setDatos(null); }}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Hasta</label>
            <input type="date" value={hasta}
              onChange={e => { setHasta(e.target.value); setDatos(null); }}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex flex-col flex-1 bg-gray-100 overflow-hidden">

      {/* Barra título + descarga — solo desktop */}
      <div className="hidden md:flex items-center gap-3 bg-white border-b border-gray-200 px-4 py-3 shadow-sm shrink-0">
        <span className="text-base font-bold text-gray-900">Informes de Siniestros</span>
        {datos && (
          <div className="ml-auto">
            <PDFDownloadLink document={pdfDoc}
              fileName={`informe-siniestros-${desde}-${hasta}.pdf`}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition shadow-sm">
              {({ loading }) => (
                <>
                  {loading
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>
                    : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                      </svg>
                  }
                  {loading ? 'Preparando...' : 'Descargar PDF'}
                </>
              )}
            </PDFDownloadLink>
          </div>
        )}
      </div>

      <div className="flex flex-col md:flex-row flex-1 md:overflow-hidden overflow-y-auto">

        {/* ── Panel filtros: desktop sidebar, móvil sección completa ── */}
        <div className="w-full md:w-64 shrink-0 bg-white md:border-r border-gray-200 p-4 space-y-5 md:overflow-y-auto border-b md:border-b-0">

          {Filtros}

          {/* ── Botón móvil: genera + descarga en un paso ── */}
          <div className="md:hidden">
            <button onClick={handleGenerarYDescargar} disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold
                         py-3 rounded-xl text-sm transition flex items-center justify-center gap-2">
              {isLoading
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>
                : <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                  </svg>
              }
              {isLoading ? loadingMsg || 'Generando...' : 'Generar y descargar PDF'}
            </button>

            {isLoading && (
              <p className="text-xs text-blue-600 text-center mt-2">Esto puede tardar unos segundos</p>
            )}
            {datos && !isLoading && (
              <p className="text-xs text-green-700 text-center mt-2">{datos.length} siniestros — PDF descargado</p>
            )}
          </div>

          {/* ── Botón desktop: solo genera para el visor ── */}
          <div className="hidden md:block">
            <button onClick={handleGenerar} disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold
                         py-2.5 rounded-xl text-sm transition flex items-center justify-center gap-2">
              {isLoading
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>
                : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
              }
              {isLoading ? 'Generando...' : 'Generar informe'}
            </button>
            {datos && !isLoading && (
              <p className="text-xs text-green-700 text-center mt-2">{datos.length} siniestros encontrados</p>
            )}
          </div>

          {error && <p className="text-xs text-red-600 text-center">{error}</p>}
        </div>

        {/* ── Visor PDF — solo desktop ── */}
        <div className="hidden md:flex flex-1 items-center justify-center bg-gray-200">
          {!datos && !isLoading && (
            <div className="text-center text-gray-400">
              <svg className="w-16 h-16 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <p className="font-medium">Selecciona filtros y pulsa "Generar informe"</p>
            </div>
          )}
          {isLoading && <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"/>}
          {datos && pdfDoc && (
            <PDFViewer className="w-full h-full border-0" showToolbar={false}>
              {pdfDoc}
            </PDFViewer>
          )}
        </div>
      </div>
    </div>
  );
}
